import type { RequiredIfOptions } from "@edaz/services/validators/requiredIf";
import requiredIf from "@edaz/services/validators/requiredIf";
import { bound } from "@frui.ts/helpers/dist";
import type { ITypedEntityValidationRules } from "@frui.ts/validation/dist";
import BaseValidationService, * as baseValidationService from "@edaz/services/baseValidationService";
import type { EqualWithOptions } from "@edaz/services/validators/equalWith";
import { equalWith } from "@edaz/services/validators/equalWith";
import type { RangeOptions } from "validators/range";
import range from "validators/range";
import { isPhoneNumber } from "@edaz/services/validators/isPhoneNumber";
import type { IsLicensePlateOptions } from "@edaz/services/validators/isLicensePlate";
import { isLicensePlate } from "@edaz/services/validators/isLicensePlate";
import required from "@edaz/services/validators/required";
import type { IsPhoneNumberOptions } from "@edaz/services/validators/isPhoneNumber";
import type { IsLengthOptions } from "validators/isLength";
import isLength from "validators/isLength";
import type { IsGPSCoordOptions } from "@edaz/services/validators/isGPSCoord";
import { isGPSCoord } from "@edaz/services/validators/isGPSCoord";
import { isEmail } from "@edaz/services/validators/isEmail";
import type { IsEmailOptions } from "@edaz/services/validators/isEmail";
import type { IsAfterOptions } from "../validators/isAfter";
import isAfter from "../validators/isAfter";
import type { IsAfterQuarterYearOptions } from "../validators/isAfterQuarterYear";
import isAfterQuarterYear from "../validators/isAfterQuarterYear";
import type { IsAlphaNumericTextOptions } from "../validators/isAlphanumericText";
import isAlphanumericText from "../validators/isAlphanumericText";
import type { IsBankAccountNumberOptions } from "../validators/isBankAccountNumber";
import { isBankAccountNumber } from "../validators/isBankAccountNumber";
import type { IsBankCodeOptions } from "../validators/isBankCode";
import isBankCode from "../validators/isBankCode";
import type { IsFileTypeOptions } from "../validators/isFileType";
import isFileType from "../validators/isFileType";
import type { IsIBANOptions } from "../validators/isIBAN";
import isIBAN from "../validators/isIBAN";
import { isInteger } from "../validators/isInteger";
import type { IsNumberRangeOptions } from "../validators/isNumberRange";
import isNumberRange from "../validators/isNumberRange";
import { isNutsCode } from "../validators/isNutsCode";
import isPaymentOrderText from "../validators/isPaymentOrderText";
import type LocalizationService from "./localizationService";
import type { IsBeforeOptions } from "../validators/isBefore";
import isBefore from "../validators/isBefore";
import type ManualEntityValidator from "@frui.ts/validation/dist/manualEntityValidator";
import { get } from "mobx";
import type { IsTextWithoutAccentsOptions } from "../validators/isTextWithoutAccents";
import isTextWithoutAccents from "../validators/isTextWithoutAccents";
import type { IsNotEqualOptions } from "validators/isNotEqual";
import isNotEqual from "validators/isNotEqual";
import type { IsDateRangeOptions } from "validators/isDateRange";
import isDateRange from "validators/isDateRange";
import type { IsValidRegexOptions } from "validators/isValidRegex";
import { isValidRegex } from "validators/isValidRegex";
import type { IsSimpleEmailOptions } from "validators/isSimpleEmail";
import { isSimpleEmail } from "validators/isSimpleEmail";
import type { IsSimplePhoneNumberOptions } from "validators/isSimplePhoneNumber";
import { isSimplePhoneNumber } from "validators/isSimplePhoneNumber";

export type EntityValidationRules<TTarget> = ITypedEntityValidationRules<TTarget, ValidationRules<TTarget>>;

export interface ValidationRules<TEntity = any> {
  required: boolean | string;
  requiredIf: RequiredIfOptions;
  isEmail: IsEmailOptions;
  equalWith: EqualWithOptions<TEntity>;
  range: RangeOptions;
  isICO: boolean;
  isNotEqual: IsNotEqualOptions;
  isAfter: IsAfterOptions;
  isAfterQuarterYear: IsAfterQuarterYearOptions;
  isBefore: IsBeforeOptions;
  isLength: IsLengthOptions;
  isLicensePlate: IsLicensePlateOptions | boolean;
  isPhoneNumber: IsPhoneNumberOptions;
  isGPSCoord: IsGPSCoordOptions;
  isFileType: IsFileTypeOptions;
  isAlphanumericText: boolean | string | IsAlphaNumericTextOptions;
  isTextWithoutAccents: boolean | string | IsTextWithoutAccentsOptions;
  isPaymentOrderText: boolean | string;
  isBankCode: IsBankCodeOptions;
  isBankAccountNumber: IsBankAccountNumberOptions;
  isIBAN: IsIBANOptions;
  isNutsCode: boolean;
  isNumberRange: IsNumberRangeOptions;
  isDateRange: IsDateRangeOptions;
  isInteger: boolean;
  isValidRegex: IsValidRegexOptions | boolean;
  isSimpleEmail: IsSimpleEmailOptions | boolean;
  isSimplePhoneNumber: IsSimplePhoneNumberOptions;
  manualValidation: ManualEntityValidator<any>;
}

export default class ValidationService extends BaseValidationService<ValidationRules> {
  private valueRegex = /%value%/g;
  private constraintRegex = /%param\[(\d+)\]%/g;

  constructor(private localizationService: LocalizationService) {
    super();
  }

  initializeValidators() {
    this.registerValidator("required", required);
    this.registerValidator("requiredIf", requiredIf);
    this.registerValidator("isEmail", isEmail);
    this.registerValidator("isLength", isLength);
    this.registerValidator("equalWith", equalWith);
    this.registerValidator("range", range);
    this.registerValidator("isPhoneNumber", isPhoneNumber);
    this.registerValidator("isAfter", isAfter);
    this.registerValidator("isNotEqual", isNotEqual);
    this.registerValidator("isAfterQuarterYear", isAfterQuarterYear);
    this.registerValidator("isBefore", isBefore);
    this.registerValidator("isGPSCoord", isGPSCoord);
    this.registerValidator("isLicensePlate", isLicensePlate);
    this.registerValidator("isFileType", isFileType);
    this.registerValidator("isIBAN", isIBAN);
    this.registerValidator("isAlphanumericText", isAlphanumericText);
    this.registerValidator("isTextWithoutAccents", isTextWithoutAccents);
    this.registerValidator("isPaymentOrderText", isPaymentOrderText);
    this.registerValidator("isBankCode", isBankCode);
    this.registerValidator("isBankAccountNumber", isBankAccountNumber);
    this.registerValidator("isNutsCode", isNutsCode);
    this.registerValidator("isInteger", isInteger);
    this.registerValidator("isNumberRange", isNumberRange);
    this.registerValidator("isDateRange", isDateRange);
    this.registerValidator("isValidRegex", isValidRegex);
    this.registerValidator("isSimpleEmail", isSimpleEmail);
    this.registerValidator("isSimplePhoneNumber", isSimplePhoneNumber);
    this.validators.set("manualValidation", (value, propertyName, entity, params) => get(params.errors, propertyName));
  }

  @bound
  localizedMessage(validatorName: string, errors?: baseValidationService.IValidationError, customMessage?: string) {
    const messages = [];

    if (customMessage) {
      messages.push(this.localizationService.translate(`validators.customMessages.${customMessage}`));
    } else if (errors?.constraints) {
      for (const constraint in errors?.constraints) {
        messages.push(this.localizationService.translate(`validators.${validatorName}.${constraint}`));
      }
    } else {
      messages.push(this.localizationService.translate(`validators.${validatorName}`));
    }

    return messages
      .map(message => {
        if (errors) {
          message = message.replace(this.valueRegex, errors.value);
          message = message.replace(this.constraintRegex, (match, number) => {
            if (errors.params && errors.params.length >= number) {
              return errors.params[number - 1];
            }
            return match;
          });
        }
        return message;
      })
      .join(" ");
  }
}
